
// Main effects orchestration module
// Combines audio, haptic, and visual effects

import { 
  initAudio, 
  playFireworksSound, 
  playButtonSound,
  playButtonDownSound,
  playButtonUpSound,
  playSound,
  registerSound,
  getSoundManager
} from './audio';
import { 
  primeHapticFeedback, 
  triggerHapticFeedback,
  triggerCustomHapticPattern,
  triggerBpmPulse,
  isHapticFeedbackAvailable,
  HAPTIC_PATTERNS,
  type HapticPattern
} from './haptics';
import { 
  triggerConfettiEffect, 
  triggerConfettiFromElement, 
  triggerSnowfall,
  CONFETTI_PATTERNS,
  type ConfettiConfig
} from './visual';

// Export individual components for direct access
export { 
  initAudio, 
  playFireworksSound, 
  playButtonSound,
  playButtonDownSound,
  playButtonUpSound,
  playSound,
  registerSound,
  getSoundManager,
  primeHapticFeedback,
  triggerHapticFeedback,
  triggerCustomHapticPattern,
  triggerBpmPulse,
  isHapticFeedbackAvailable,
  HAPTIC_PATTERNS,
  type HapticPattern,
  triggerConfettiEffect,
  triggerConfettiFromElement,
  triggerSnowfall,
  CONFETTI_PATTERNS,
  type ConfettiConfig
};

// Helper to check if we're on a route that doesn't need audio
const isAudioDisabledRoute = () => {
  const pathname = window.location.pathname;
  console.log("effects/index.ts: Checking if audio is disabled for current route:", pathname);
  
  // Add more verbose debugging
  const isDashboardRoute = pathname.includes('/dashboard');
  const isTeamsRoute = pathname.includes('/teams');
  const isDisabled = isDashboardRoute || isTeamsRoute;
  
  console.log(`effects/index.ts: Audio disabled check: isDashboardRoute=${isDashboardRoute}, isTeamsRoute=${isTeamsRoute}, isDisabled=${isDisabled}`);
  
  return isDisabled;
};

// Track when effects have been properly primed
let effectsPrimed = false;
let isInitializing = false;

// Initialize effects on page load to reduce delay
if (typeof window !== 'undefined') {
  let hasInitialized = false;
  
  const initializeEffects = async () => {
    // Skip for dashboard/teams pages
    if (isAudioDisabledRoute()) {
      console.log("⛔ effects/index.ts: Skipping effects initialization for dashboard/teams pages");
      return;
    }
    
    if (hasInitialized || isInitializing) return;
    isInitializing = true;
    console.log("effects/index.ts: Initializing effects on page load or user interaction");
    
    // Initialize audio with debugging
    try {
      const success = await initAudio();
      console.log("effects/index.ts: Audio initialization during page load:", success ? "successful" : "failed");
      
      // Prime haptics
      primeHapticFeedback();
      
      hasInitialized = true;
    } catch (error) {
      console.error("effects/index.ts: Error initializing effects:", error);
    } finally {
      isInitializing = false;
    }
  };
  
  // Only proceed with initialization if not on dashboard/teams routes
  if (!isAudioDisabledRoute()) {
    // Try to initialize on DOM content loaded
    window.addEventListener('DOMContentLoaded', () => {
      console.log("DOM content loaded, initializing effects");
      initializeEffects();
    });
    
    // Also try on full page load
    window.addEventListener('load', () => {
      console.log("Window load event, initializing effects");
      setTimeout(initializeEffects, 100);
    });
    
    // Add multiple event listeners to ensure initialization happens on user interaction
    const userInteractionEvents = ['touchstart', 'mousedown', 'pointerdown', 'keydown'];
    
    userInteractionEvents.forEach(eventType => {
      window.addEventListener(eventType, () => {
        // Skip for dashboard/teams pages
        if (isAudioDisabledRoute()) {
          console.log(`⛔ effects/index.ts: Skipping effects init on ${eventType} - dashboard/teams route`);
          return;
        }
        
        console.log(`User interaction (${eventType}), initializing effects`);
        initializeEffects();
      }, { once: true });
    });
  } else {
    console.log("⛔ effects/index.ts: Skipping ALL effects initialization for dashboard/teams pages during module load");
  }
  
  // Special handling for iOS and Safari
  document.addEventListener('visibilitychange', () => {
    // Skip for dashboard/teams pages
    if (isAudioDisabledRoute()) {
      console.log("⛔ effects/index.ts: Skipping effects on visibility change - dashboard/teams route");
      return;
    }
    
    if (document.visibilityState === 'visible') {
      console.log("Page visibility changed to visible, re-initializing effects");
      initializeEffects();
    }
  });
}

// Main combined effects function - use this for the full celebration effect
export const triggerConfetti = (): void => {
  // Skip for dashboard/teams pages
  if (isAudioDisabledRoute()) {
    console.log("⛔ effects/index.ts: Skipping confetti effect on dashboard/teams route");
    return;
  }
  
  console.log("Triggering full confetti celebration effect");
  
  // Make sure haptics are primed before trying to trigger
  primeHapticFeedback();
  
  // Trigger haptics first so it feels more responsive
  triggerHapticFeedback('success');
  
  // Trigger visual confetti
  triggerConfettiEffect();
  
  // Play sound immediately after to synchronize
  playFireworksSound();
};

// Trigger confetti with a specific pattern
export const triggerConfettiPattern = (patternName: keyof typeof CONFETTI_PATTERNS): void => {
  // Skip for dashboard/teams pages
  if (isAudioDisabledRoute()) return;
  
  // Make sure haptics are primed
  primeHapticFeedback();
  
  // Trigger haptics first for better responsiveness
  triggerHapticFeedback('success');
  
  // Trigger visual confetti with the selected pattern
  triggerConfettiEffect(CONFETTI_PATTERNS[patternName]);
  
  // Play sound immediately after
  playFireworksSound();
};

// Trigger confetti from a specific element with full effects
export const triggerConfettiFromElementWithEffects = (element: HTMLElement | null): void => {
  // Skip for dashboard/teams pages
  if (isAudioDisabledRoute()) return;
  
  // Make sure haptics are primed
  primeHapticFeedback();
  
  // Trigger haptics first for better responsiveness
  triggerHapticFeedback('success');
  
  // Trigger visual confetti from element
  triggerConfettiFromElement(element);
  
  // Play sound immediately after
  playFireworksSound();
};

// Export a function to prime all effects on user interaction
export const primeEffects = async (): Promise<boolean> => {
  // Skip for dashboard/teams pages
  if (isAudioDisabledRoute()) {
    console.log("⛔ effects/index.ts: Skipping primeEffects for dashboard/teams pages");
    return true;
  }
  
  if (isInitializing) {
    console.log("Audio initialization already in progress during primeEffects");
    // Still return true to allow sounds to play
    return true;
  }
  
  console.log("Explicitly priming effects...");
  
  // If already primed, skip the heavy work
  if (effectsPrimed) {
    console.log("Effects already primed, skipping");
    return true;
  }
  
  isInitializing = true;
  
  // Initialize audio with a more verbose log for debugging
  try {
    const success = await initAudio();
    console.log("Audio initialized during primeEffects:", success ? "SUCCESS" : "FAILED");
    
    if (success) {
      // Pre-load specific sound buffers for immediate use
      const soundManager = getSoundManager();
      
      // Pre-load button down sound first with highest priority
      await soundManager.preloadSound('buttonDown', true);
      
      // Then load the rest
      Promise.allSettled([
        soundManager.preloadSound('buttonClick', true),
        soundManager.preloadSound('buttonUp', true),
        soundManager.preloadSound('fireworks', false)
      ]);
      
      // Mark as fully primed
      effectsPrimed = true;
    }
  } catch (error) {
    console.error("Error initializing audio in primeEffects:", error);
  } finally {
    isInitializing = false;
  }
  
  // Prime haptics with minimal vibration
  primeHapticFeedback();
  
  return effectsPrimed || true; // Return true to allow sounds to play even if not fully primed
};
